import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUniverses } from '../../hooks/useChancellery';
import { CreateUniverseWizard } from '../../components/chancellery/CreateUniverseWizard';
import { COLORS } from '../../constants/theme';
import type { AgeStatus, Universe } from '../../types/chancellery';

const STATUS_COLOR: Record<AgeStatus, string> = {
  ACTIVE: COLORS.positive,
  PAUSED: COLORS.warning,
  PENDING: COLORS.text.tertiary,
};

function totalKingdoms(universe: Universe): number {
  return universe.worlds.reduce((sum, world) => sum + world.kingdomCount, 0);
}

/** Chancellery universes list — route: /chancellery */
export function UniversesList() {
  const { data: universes } = useUniverses();
  const navigate = useNavigate();
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold mb-1" style={{ color: COLORS.text.primary }}>
            Universes
          </h1>
          <p className="text-sm" style={{ color: COLORS.text.tertiary }}>
            Every ruleset your realm runs under, and the ladder of worlds inside each one.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIsWizardOpen(true)}
          className="px-4 py-2 rounded font-medium text-sm"
          style={{ backgroundColor: COLORS.btnPrimary, color: COLORS.text.primary }}
        >
          + New Universe
        </button>
      </div>

      {!universes || universes.length === 0 ? (
        <p className="text-sm" style={{ color: COLORS.text.tertiary }}>
          No universes yet.
        </p>
      ) : (
        <div className="grid gap-3">
          {universes.map((universe) => (
            <button
              key={universe.universeId}
              type="button"
              onClick={() => navigate(`/chancellery/universes/${universe.universeId}`)}
              className="flex items-center justify-between text-left px-5 py-4 rounded transition-colors"
              style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
            >
              <div className="flex flex-col gap-1">
                <span className="text-base font-semibold" style={{ color: COLORS.text.primary }}>
                  {universe.name}
                </span>
                <span className="text-sm" style={{ color: COLORS.text.tertiary }}>
                  {universe.description}
                </span>
                <span className="flex items-center gap-2 text-xs mt-1" style={{ color: COLORS.text.tertiary }}>
                  <span>{universe.worlds.length} tiers</span>
                  <span>·</span>
                  <span>
                    Age {universe.currentAge.ageNumber}{' '}
                    <span style={{ color: STATUS_COLOR[universe.currentAge.status], fontWeight: 600 }}>
                      {universe.currentAge.status === 'ACTIVE'
                        ? 'Active'
                        : universe.currentAge.status === 'PAUSED'
                          ? 'Paused'
                          : 'Pending'}
                    </span>
                  </span>
                  <span>·</span>
                  <span>{totalKingdoms(universe)} kingdoms</span>
                  {universe.sandbox && (
                    <>
                      <span>·</span>
                      <span style={{ color: COLORS.link, fontWeight: 600 }}>
                        Sandbox: {universe.sandbox.name}
                      </span>
                    </>
                  )}
                </span>
              </div>
              <span aria-hidden="true" style={{ color: COLORS.text.tertiary }}>
                →
              </span>
            </button>
          ))}
        </div>
      )}

      <CreateUniverseWizard isOpen={isWizardOpen} onClose={() => setIsWizardOpen(false)} />
    </div>
  );
}
